import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  DEFAULT_LIVE_STREAM_TRANSPORT,
  resolveRelayStreamUrl,
  startCameraRelay,
  type Camera,
  type CameraRelayUrls,
} from "../../api/cameras.ts";
import { ApiRequestError } from "../../api/client.ts";
import {
  fetchPreviewPrefs,
  fetchPtzPrefs,
  savePreviewPrefs,
  savePtzPrefs,
  type PreviewPrefsPatch,
  type PtzPrefsPatch,
} from "../../api/preferences.ts";
import { fetchMyCameraAccess } from "../../api/cameraAccess.ts";
import {
  fetchPtzLimits,
  fetchPtzState,
  ptzAbsoluteMove,
  ptzFocusFromRing,
  ptzRelativeMove,
  ptzResetZoom,
  type PtzLimits,
  type PtzState,
} from "../../api/ptz.ts";
import {
  callCameraPreset,
  fetchCameraPresets,
  type CameraPreset,
} from "../../api/ptzPresets.ts";
import { useAuth } from "../../lib/place360Auth.tsx";
import {
  getCachedPreviewPrefs,
  getFocusRing,
  getMainLens,
  getPipPopupHidden,
  getView360Mode,
  PIP_SHARE_SHOW_PTZ_EDGE_ARROWS_AT,
  PIP_SHARE_MAX,
  PIP_SHARE_MIN,
  setCachedPreviewPrefs,
  setPipPopupHidden as persistPipPopupHidden,
  persistFocus,
  persistMainLens,
  persistView360Mode,
  type MainLens,
  type View360Mode,
} from "../../lib/previewSession.ts";
import {
  getCachedPtzPrefs,
  PTZ_BUFFER_MAX_MAX,
  PTZ_BUFFER_MAX_MIN,
  setCachedPtzPrefs,
} from "../../lib/ptzPrefs.ts";
import {
  adjustNormYForPtzAim,
  focusRingDiameterFrac,
  ptzStateToRingNorm,
} from "../../lib/ptzPanoramaMap.ts";
import {
  usePtzContinuousControl,
  type PtzDir,
} from "../../lib/usePtzContinuousControl.ts";
import { usePtzWheelZoom } from "../../lib/ptzZoomWheel.ts";
import { PanoramaHorizonLine } from "./PanoramaHorizonLine.tsx";
import { Pano360WebGL, type Pano360ImmersiveScene } from "./Pano360WebGL.tsx";
import { PreviewActivityBar } from "./PreviewActivityBar.tsx";
import { PtzButton } from "./PtzButton.tsx";
import { PtzEdgeDirectionPad } from "./PtzEdgeDirectionPad.tsx";
import { PtzGamepadDpad } from "./PtzGamepadDpad.tsx";
import { PtzZoomPanel } from "./PtzZoomPanel.tsx";
import { PtzMoveLoader } from "./PtzMoveLoader.tsx";
import { StreamVideo } from "./StreamVideo.tsx";
import { useCamera360Hotspots } from "../../hooks/useCamera360Hotspots.ts";
import { PatrolAssetHotspotsLayer } from "../../pages/ConstructionSiteManagement/patrol/PatrolAssetHotspotsLayer.jsx";
import type * as THREE from "three";
import { PRESET_GRID_TOTAL } from "../../lib/presetGrid.ts";
import {
  normalizeCameraVendor,
  usesAbsolutePtzMode,
} from "../../lib/cameraVendor.ts";
import { usePtzAbsoluteStepControl } from "../../lib/usePtzAbsoluteStepControl.ts";
const PIP_EDGE_INSET = 8;
const PREFS_PUT_DEBOUNCE_MS = 500;
const PAN_DEG_PER_SEC = 180;
const TILT_DEG_PER_SEC = 120;
const ZOOM_X_PER_SEC = 6;
const HLS_CATCHUP_MS = 1500;
const MIN_LOADER_VISIBLE_MS = 1100;
const MAX_LOADER_VISIBLE_MS = 8000;

function estimatePtzTravelMs(prev: PtzState, next: PtzState): number {
  const rawPan = Math.abs(next.pan - prev.pan);
  const dPan = Math.min(rawPan, 360 - rawPan);
  const dTilt = Math.abs(next.tilt - prev.tilt);
  const dZoom = Math.abs(next.zoom - prev.zoom);
  const panMs = (dPan / PAN_DEG_PER_SEC) * 1000;
  const tiltMs = (dTilt / TILT_DEG_PER_SEC) * 1000;
  const zoomMs = (dZoom / ZOOM_X_PER_SEC) * 1000;
  return Math.max(panMs, tiltMs, zoomMs);
}

const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
} as const;

function Icon360() {
  return (
    <svg {...iconProps}>
      <ellipse cx="12" cy="12" rx="10" ry="5" />
      <path d="M2 12c0-2.8 4.5-5 10-5" />
    </svg>
  );
}
function IconFlat() {
  return (
    <svg {...iconProps}>
      <rect x="3" y="6" width="18" height="12" rx="1.5" />
      <path d="M3 12h18" />
    </svg>
  );
}
function IconSwitchCamera() {
  return (
    <svg {...iconProps}>
      <path d="M4 7h13l-2-2" />
      <path d="M20 17H7l2 2" />
    </svg>
  );
}
function IconFullscreen() {
  return (
    <svg {...iconProps}>
      <path d="M4 9V4h5" />
      <path d="M20 9V4h-5" />
      <path d="M4 15v5h5" />
      <path d="M20 15v5h-5" />
    </svg>
  );
}
function IconExitFullscreen() {
  return (
    <svg {...iconProps}>
      <path d="M9 4v5H4" />
      <path d="M15 4v5h5" />
      <path d="M9 20v-5H4" />
      <path d="M15 20v-5h5" />
    </svg>
  );
}
function IconSliders() {
  return (
    <svg {...iconProps}>
      <path d="M4 6h10" />
      <path d="M18 6h2" />
      <path d="M4 12h2" />
      <path d="M10 12h10" />
      <path d="M4 18h14" />
      <path d="M20 18h0" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="19" cy="18" r="2" />
    </svg>
  );
}

function IconPipPopupVisible() {
  return (
    <svg {...iconProps}>
      <rect x="3" y="5" width="12" height="10" rx="1.5" />
      <rect x="14" y="9" width="7" height="6" rx="1" opacity={0.95} />
    </svg>
  );
}

function IconPipPopupHidden() {
  return (
    <svg {...iconProps}>
      <rect x="3" y="5" width="12" height="10" rx="1.5" />
      <rect x="14" y="9" width="7" height="6" rx="1" opacity={0.45} />
      <path d="M4 20 20 4" strokeLinecap="round" />
    </svg>
  );
}

export type DualCameraPreviewProps = {
  camera: Camera;
  controlsHostRef?: RefObject<HTMLElement | null>;
  accessReloadTick?: number;
};

type StreamKey = "360" | "ptz";
type StreamTransport = "hls" | "webrtc";

const DEFAULT_STREAM_TRANSPORT: StreamTransport = DEFAULT_LIVE_STREAM_TRANSPORT;

function DualCameraPreviewNoStreams({ camera }: { camera: Camera }) {
  return (
    <div className="ds-preview-root">
      <div className="ds-preview-toolbar">
        <h1 className="ds-page-title" style={{ margin: 0 }}>
          {camera.name}
        </h1>
      </div>
      <div className="ds-preview-stream-fallback" role="alert">
        <p>No RTSP URLs configured for this camera.</p>
        <p className="ds-text-secondary" style={{ marginTop: 8 }}>
          Add a 360° or PTZ RTSP URL on the Cameras tab, then return to Live
          View.
        </p>
      </div>
    </div>
  );
}

export function DualCameraPreview(props: DualCameraPreviewProps) {
  const has360 = Boolean(props.camera.cam360Rtsp?.trim());
  const hasPtz = Boolean(props.camera.ptzRtsp?.trim());
  if (!has360 && !hasPtz) {
    return <DualCameraPreviewNoStreams camera={props.camera} />;
  }
  return <DualCameraPreviewBody {...props} />;
}

function DualCameraPreviewBody({
  camera,
  controlsHostRef,
  accessReloadTick = 0,
}: DualCameraPreviewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const isAdmin = user?.role === "admin";

  const [accessGate, setAccessGate] = useState<{
    loaded: boolean;
    ptzAllowed: boolean;
    status: "none" | "pending" | "active" | "blocked";
    blockedUntil: string | null;
  }>({ loaded: false, ptzAllowed: false, status: "none", blockedUntil: null });

  useEffect(() => {
    if (!camera.id) return;
    if (isAdmin) {
      setAccessGate({
        loaded: true,
        ptzAllowed: true,
        status: "active",
        blockedUntil: null,
      });
      return;
    }
    if (!userId) {
      setAccessGate({
        loaded: false,
        ptzAllowed: false,
        status: "none",
        blockedUntil: null,
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetchMyCameraAccess(camera.id);
        if (cancelled) return;
        setAccessGate({
          loaded: true,
          ptzAllowed: r.ptzAllowed,
          status: r.status,
          blockedUntil: r.blockedUntil,
        });
      } catch {
        if (cancelled) return;
        setAccessGate({
          loaded: true,
          ptzAllowed: false,
          status: "none",
          blockedUntil: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [camera.id, isAdmin, userId, accessReloadTick]);

  const [mainLens, setMainLensState] = useState<MainLens>(() => getMainLens());
  const [view360, setView360State] = useState<View360Mode>(() =>
    getView360Mode(),
  );
  const [focus, setFocus] = useState(() => getFocusRing());
  const focusRef = useRef(focus);
  focusRef.current = focus;
  const panUnwrapHintRef = useRef<number | null>(null);
  /** Keep ring at click coords; only server normY is adjusted below the horizon dash. */
  const ringDisplayPinnedRef = useRef(false);

  const initialPrefs = useMemo(() => getCachedPreviewPrefs(userId), [userId]);
  const [pipSharePct, setPipSharePctState] = useState<number>(
    initialPrefs.pipSharePct,
  );
  const [stageRect, setStageRect] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 },
  );
  const [pipPos, setPipPosState] = useState(initialPrefs.pipPosition);
  const [ptz, setPtz] = useState<PtzState>({ pan: 0, tilt: 0, zoom: 5 });
  const ptzRef = useRef<PtzState>(ptz);
  useEffect(() => {
    ptzRef.current = ptz;
  }, [ptz]);

  const mergeZoomFromHardware = useCallback((zoomValue: number) => {
    const z = Math.min(25, Math.max(1, Math.round(zoomValue)));
    setFocus((prev) => {
      if (Math.round(prev.zoom) === z) return prev;
      const merged = { ...prev, zoom: z };
      persistFocus(merged);
      return merged;
    });
  }, []);

  const [ptzLimits, setPtzLimits] = useState<PtzLimits | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const initialPtzPrefs = useMemo(() => getCachedPtzPrefs(userId), [userId]);
  const [bufferMax, setBufferMaxState] = useState<number>(
    initialPtzPrefs.bufferMax,
  );
  const [streamFault, setStreamFault] = useState<Record<StreamKey, boolean>>({
    "360": false,
    ptz: false,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [streamAutoRetry, setStreamAutoRetry] = useState<
    Record<StreamKey, number>
  >({ "360": 0, ptz: 0 });
  const streamAutoRetryCountRef = useRef<Record<StreamKey, number>>({
    "360": 0,
    ptz: 0,
  });
  const [relayUrls, setRelayUrls] = useState<CameraRelayUrls | null>(null);
  const [stream360Transport, setStream360Transport] = useState<StreamTransport>(
    DEFAULT_STREAM_TRANSPORT,
  );
  const [streamPtzTransport, setStreamPtzTransport] = useState<StreamTransport>(
    DEFAULT_STREAM_TRANSPORT,
  );
  useEffect(() => {
    streamAutoRetryCountRef.current["360"] = 0;
    setStreamAutoRetry((prev) => ({ ...prev, "360": 0 }));
    setStreamFault((prev) => ({ ...prev, "360": false }));
  }, [stream360Transport]);
  useEffect(() => {
    streamAutoRetryCountRef.current.ptz = 0;
    setStreamAutoRetry((prev) => ({ ...prev, ptz: 0 }));
    setStreamFault((prev) => ({ ...prev, ptz: false }));
  }, [streamPtzTransport]);
  const [relayBootstrapError, setRelayBootstrapError] = useState<string | null>(
    null,
  );
  const [buffering, setBuffering] = useState<Record<StreamKey, boolean>>({
    "360": false,
    ptz: false,
  });
  const [streamInitializing, setStreamInitializing] = useState<
    Record<StreamKey, boolean>
  >({
    "360": false,
    ptz: false,
  });
  const [draggingPip, setDraggingPip] = useState(false);
  const [pipPopupHidden, setPipPopupHiddenState] = useState(() =>
    getPipPopupHidden(),
  );
  const dragRef = useRef<{
    dx: number;
    dy: number;
    startL: number;
    startT: number;
  } | null>(null);
  const [ptzMoving, setPtzMoving] = useState(false);
  const ptzMoveSeqRef = useRef(0);
  const ptzMovingRef = useRef(false);
  useEffect(() => {
    ptzMovingRef.current = ptzMoving;
  }, [ptzMoving]);

  const has360 = Boolean(camera.cam360Rtsp?.trim());
  const hasPtz = Boolean(camera.ptzRtsp?.trim());
  const ptzOperable = Boolean(
    hasPtz && (isAdmin || (accessGate.loaded && accessGate.ptzAllowed)),
  );

  const accessBannerText = (() => {
    if (!accessGate.loaded || isAdmin || ptzOperable) return null;
    if (accessGate.status === "pending") {
      return "PTZ / ONVIF controls are off until an admin approves your access request.";
    }
    if (accessGate.status === "blocked") {
      if (accessGate.blockedUntil) {
        try {
          return `PTZ is blocked until ${new Date(accessGate.blockedUntil).toLocaleString()}, then it may unlock automatically. You can still submit a new access request in Live View for an admin to review.`;
        } catch {
          return "PTZ is blocked for this camera. You can submit a new access request in Live View for an admin to review.";
        }
      }
      return "PTZ is blocked for this camera. Submit a new access request in Live View (above the preview) for an admin to review.";
    }
    return "PTZ / ONVIF controls are off. Use “Request access” in Live View, then wait for an admin.";
  })();

  useEffect(() => {
    if (!has360 && hasPtz && mainLens === "360") {
      setMainLensState("ptz");
      persistMainLens("ptz");
    } else if (!hasPtz && has360 && mainLens === "ptz") {
      setMainLensState("360");
      persistMainLens("360");
    }
  }, [has360, hasPtz, mainLens]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const { assetHotspots } = useCamera360Hotspots(camera.id);
  const [cameraPresets, setCameraPresets] = useState<CameraPreset[]>([]);
  const [selectedPresetGridId, setSelectedPresetGridId] = useState<
    number | null
  >(null);

  const immersiveHostRef = useRef<HTMLDivElement | null>(null);
  const immersiveCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const immersiveCameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const [immersiveSceneReady, setImmersiveSceneReady] = useState(false);
  const [hotspotViewOpen, setHotspotViewOpen] = useState(false);

  const handleImmersiveSceneReady = useCallback(
    (scene: Pano360ImmersiveScene | null) => {
      immersiveHostRef.current = scene?.host ?? null;
      immersiveCanvasRef.current = scene?.canvas ?? null;
      immersiveCameraRef.current = scene?.camera ?? null;
      setImmersiveSceneReady(Boolean(scene?.host));
    },
    [],
  );

  const showImmersiveAssetHotspots =
    mainLens === "360" &&
    view360 === "immersive" &&
    assetHotspots.length > 0 &&
    immersiveSceneReady;

  useEffect(() => {
    if (mainLens !== "360" || view360 !== "immersive") {
      setImmersiveSceneReady(false);
    }
  }, [mainLens, view360]);

  useEffect(() => {
    let cancelled = false;
    if (!camera.id) {
      setCameraPresets([]);
      setSelectedPresetGridId(null);
      return;
    }
    void (async () => {
      try {
        const rows = await fetchCameraPresets(camera.id);
        if (cancelled) return;
        setCameraPresets(rows);
      } catch {
        if (cancelled) return;
        setCameraPresets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [camera.id]);

  const handlePresetGridSelect = useCallback(
    async (gridId: number) => {
      setSelectedPresetGridId(gridId);
      if (!hasPtz) {
        showToast("PTZ stream is required for preset move.");
        return;
      }
      if (!ptzOperable) {
        showToast(
          "PTZ / ONVIF is locked until an admin approves access for you on this camera.",
        );
        return;
      }
      const hasMapping = cameraPresets.some((row) => row.grid_id === gridId);
      if (!hasMapping) {
        showToast(`No preset mapping saved for grid ${gridId}.`);
        return;
      }
      setPtzMoving(true);
      try {
        await callCameraPreset({
          cameraid: camera.id,
          gridid: gridId,
        });
        const s = await fetchPtzState(camera.id);
        setPtz(s);
        mergeZoomFromHardware(s.zoom);
      } catch (e) {
        if (e instanceof ApiRequestError) {
          showToast(e.message || "Could not move to preset.");
        } else {
          showToast("Could not move to preset.");
        }
      } finally {
        setPtzMoving(false);
      }
    },
    [
      camera.id,
      cameraPresets,
      hasPtz,
      mergeZoomFromHardware,
      ptzOperable,
      showToast,
    ],
  );

  useEffect(() => {
    if (!userId) return;
    const cached = getCachedPreviewPrefs(userId);
    setPipSharePctState(cached.pipSharePct);
    setPipPosState(cached.pipPosition);
    let cancelled = false;
    void (async () => {
      try {
        const server = await fetchPreviewPrefs();
        if (cancelled) return;
        setPipSharePctState(server.pipSharePct);
        setPipPosState(server.pipPosition);
        setCachedPreviewPrefs(userId, server);
      } catch {
        /* keep cache */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const cached = getCachedPtzPrefs(userId);
    setBufferMaxState(cached.bufferMax);
    let cancelled = false;
    void (async () => {
      try {
        const server = await fetchPtzPrefs();
        if (cancelled) return;
        setBufferMaxState(server.bufferMax);
        setCachedPtzPrefs(userId, server);
      } catch {
        /* keep cache */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const pendingPatchRef = useRef<PreviewPrefsPatch>({});
  const putTimerRef = useRef<number | null>(null);
  const pendingPtzPatchRef = useRef<PtzPrefsPatch>({});
  const ptzPutTimerRef = useRef<number | null>(null);

  const flushPrefsPut = useCallback(() => {
    if (putTimerRef.current !== null) {
      window.clearTimeout(putTimerRef.current);
      putTimerRef.current = null;
    }
    const patch = pendingPatchRef.current;
    pendingPatchRef.current = {};
    if (!userId) return;
    if (patch.pipSharePct === undefined && patch.pipPosition === undefined)
      return;
    void savePreviewPrefs(patch).catch((err) => {
      if (err instanceof ApiRequestError && err.status >= 500) {
        console.warn("[preview-prefs] server save failed", err);
      }
    });
  }, [userId]);

  const queuePrefsPut = useCallback(
    (patch: PreviewPrefsPatch) => {
      pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
      if (putTimerRef.current !== null)
        window.clearTimeout(putTimerRef.current);
      putTimerRef.current = window.setTimeout(
        flushPrefsPut,
        PREFS_PUT_DEBOUNCE_MS,
      );
    },
    [flushPrefsPut],
  );

  const flushPtzPrefsPut = useCallback(() => {
    if (ptzPutTimerRef.current !== null) {
      window.clearTimeout(ptzPutTimerRef.current);
      ptzPutTimerRef.current = null;
    }
    const patch = pendingPtzPatchRef.current;
    pendingPtzPatchRef.current = {};
    if (!userId) return;
    if (patch.bufferMax === undefined) return;
    void savePtzPrefs(patch).catch((err) => {
      if (err instanceof ApiRequestError && err.status >= 500) {
        console.warn("[ptz-prefs] server save failed", err);
      }
    });
  }, [userId]);

  const queuePtzPrefsPut = useCallback(
    (patch: PtzPrefsPatch) => {
      pendingPtzPatchRef.current = { ...pendingPtzPatchRef.current, ...patch };
      if (ptzPutTimerRef.current !== null)
        window.clearTimeout(ptzPutTimerRef.current);
      ptzPutTimerRef.current = window.setTimeout(
        flushPtzPrefsPut,
        PREFS_PUT_DEBOUNCE_MS,
      );
    },
    [flushPtzPrefsPut],
  );

  useEffect(() => {
    const onBeforeUnload = () => {
      flushPrefsPut();
      flushPtzPrefsPut();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      flushPrefsPut();
      flushPtzPrefsPut();
    };
  }, [flushPrefsPut, flushPtzPrefsPut]);

  const measureStage = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const read = () => {
      const r = stage.getBoundingClientRect();
      setStageRect((prev) =>
        prev.width === r.width && prev.height === r.height
          ? prev
          : { width: r.width, height: r.height },
      );
    };
    read();
    requestAnimationFrame(read);
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    measureStage();
    const ro = new ResizeObserver(() => measureStage());
    ro.observe(stage);
    window.addEventListener("resize", measureStage);
    window.addEventListener("orientationchange", measureStage);
    document.addEventListener("fullscreenchange", measureStage);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureStage);
      window.removeEventListener("orientationchange", measureStage);
      document.removeEventListener("fullscreenchange", measureStage);
    };
  }, [measureStage]);

  useLayoutEffect(() => {
    measureStage();
  }, [view360, mainLens, measureStage]);

  const pipPx = useMemo(() => {
    if (stageRect.width <= 0 || stageRect.height <= 0) {
      return { width: 0, height: 0, left: 0, top: 0, maxLeft: 0, maxTop: 0 };
    }
    const share = pipSharePct / 100;
    const width = Math.max(0, stageRect.width * share);
    const height = Math.max(0, stageRect.height * share);
    const maxLeft = Math.max(
      PIP_EDGE_INSET,
      stageRect.width - width - PIP_EDGE_INSET,
    );
    const maxTop = Math.max(
      PIP_EDGE_INSET,
      stageRect.height - height - PIP_EDGE_INSET,
    );
    const desiredLeft = pipPos.leftPct * stageRect.width;
    const desiredTop = pipPos.topPct * stageRect.height;
    return {
      width,
      height,
      left: Math.min(maxLeft, Math.max(PIP_EDGE_INSET, desiredLeft)),
      top: Math.min(maxTop, Math.max(PIP_EDGE_INSET, desiredTop)),
      maxLeft,
      maxTop,
    };
  }, [
    pipPos.leftPct,
    pipPos.topPct,
    pipSharePct,
    stageRect.height,
    stageRect.width,
  ]);

  /** 360° stream (selected transport) ready — gates immersive click-to-aim. */
  const flatPanoramaAimEnabled = useMemo(() => {
    if (!has360) return false;
    if (relayBootstrapError && relayUrls === null) return false;
    if (relayUrls === null) return false;
    if (streamFault["360"]) return false;
    const url =
      stream360Transport === "webrtc"
        ? relayUrls?.webrtc360
        : relayUrls?.hls360;
    return Boolean(url && String(url).trim());
  }, [has360, relayBootstrapError, relayUrls, stream360Transport, streamFault]);

  const ptzStreamReady = useMemo(() => {
    if (!hasPtz) return false;
    if (relayBootstrapError && relayUrls === null) return false;
    if (relayUrls === null) return false;
    if (streamFault.ptz) return false;
    const url =
      streamPtzTransport === "webrtc"
        ? relayUrls?.webrtcPtz
        : relayUrls?.hlsPtz;
    return Boolean(url && String(url).trim());
  }, [hasPtz, relayBootstrapError, relayUrls, streamPtzTransport, streamFault]);

  useEffect(() => {
    if (!has360 && !hasPtz) return;
    let cancelled = false;
    setRelayUrls(null);
    setRelayBootstrapError(null);
    void (async () => {
      try {
        const urls = await startCameraRelay(camera.id);
        if (!cancelled) setRelayUrls(urls);
      } catch (e) {
        const msg =
          e instanceof ApiRequestError
            ? e.message
            : "Could not start stream relay.";
        if (!cancelled) {
          setRelayBootstrapError(msg);
          showToast("Camera feed unavailable");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [camera.id, has360, hasPtz, reloadKey, showToast, accessReloadTick]);

  useEffect(() => {
    setStreamInitializing({ "360": false, ptz: false });
  }, [camera.id, reloadKey]);

  const setStreamInitializingFor = useCallback(
    (kind: StreamKey, loading: boolean) => {
      setStreamInitializing((prev) => ({ ...prev, [kind]: loading }));
    },
    [],
  );

  const ptzCmd = useCallback(
    async (fn: () => Promise<PtzState>) => {
      try {
        const next = await fn();
        setPtz({ pan: next.pan, tilt: next.tilt, zoom: next.zoom });
        mergeZoomFromHardware(next.zoom);
        if (next.hardwareWarning?.trim()) showToast(next.hardwareWarning);
      } catch (e) {
        if (e instanceof ApiRequestError) {
          showToast(e.message || "Action could not be completed");
        } else {
          showToast("Action could not be completed");
        }
      }
    },
    [mergeZoomFromHardware, showToast],
  );

  const ptzZoomSliderMin = ptzLimits ? Math.round(ptzLimits.zoom.min) : 1;
  const ptzZoomSliderMax = ptzLimits ? Math.round(ptzLimits.zoom.max) : 25;
  const focusRingFrac = useMemo(
    () => focusRingDiameterFrac(1, ptzZoomSliderMin, ptzZoomSliderMax),
    [ptzZoomSliderMin, ptzZoomSliderMax],
  );

  const ptzZoomRef = useRef(ptz.zoom);
  ptzZoomRef.current = ptz.zoom;
  const ptzStateRef = useRef(ptz);
  ptzStateRef.current = ptz;

  const absolutePtzMode = usesAbsolutePtzMode(camera.vendor);

  const pendingZoomTargetRef = useRef<number | null>(null);
  const zoomCommitChainRef = useRef<Promise<void>>(Promise.resolve());

  const onPtzZoomCommit = useCallback(
    (target: number) => {
      if (!ptzOperable) return;
      const rounded = Math.min(
        ptzZoomSliderMax,
        Math.max(ptzZoomSliderMin, Math.round(target)),
      );
      pendingZoomTargetRef.current = rounded;
      zoomCommitChainRef.current = zoomCommitChainRef.current
        .then(async () => {
          while (pendingZoomTargetRef.current !== null) {
            const goal = pendingZoomTargetRef.current;
            pendingZoomTargetRef.current = null;
            const cur = Math.round(ptzZoomRef.current);
            if (goal === cur) continue;
            try {
              await ptzCmd(async () => {
                if (absolutePtzMode) {
                  const s = ptzStateRef.current;
                  const next = await ptzAbsoluteMove(camera.id, {
                    pan: s.pan,
                    tilt: s.tilt,
                    zoom: goal,
                  });
                  ptzZoomRef.current = next.zoom;
                  return next;
                }
                const next = await ptzRelativeMove(camera.id, {
                  dZoom: goal - cur,
                });
                ptzZoomRef.current = next.zoom;
                return next;
              });
            } catch {
              return;
            }
          }
        })
        .catch(() => undefined);
    },
    [
      absolutePtzMode,
      camera.id,
      ptzCmd,
      ptzOperable,
      ptzZoomSliderMin,
      ptzZoomSliderMax,
    ],
  );

  const { attachWheel: attachPtzWheel } = usePtzWheelZoom({
    operable: ptzOperable,
    min: ptzZoomSliderMin,
    max: ptzZoomSliderMax,
    getZoom: () => ptzZoomRef.current,
    commitZoom: onPtzZoomCommit,
  });

  const ptzWheelSurfaceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mainLens !== "ptz") return undefined;
    return attachPtzWheel(ptzWheelSurfaceRef.current);
  }, [attachPtzWheel, mainLens]);

  const onPtzDriverResult = useCallback(
    (next: PtzState) => {
      setPtzMoving(false);
      setPtz({ pan: next.pan, tilt: next.tilt, zoom: next.zoom });
      ptzStateRef.current = next;
      mergeZoomFromHardware(next.zoom);
      if (next.hardwareWarning?.trim()) showToast(next.hardwareWarning);
    },
    [mergeZoomFromHardware, showToast],
  );

  const onPtzDriverError = useCallback(
    (e: unknown) => {
      setPtzMoving(false);
      if (e instanceof ApiRequestError) {
        showToast(e.message || "Action could not be completed");
      } else {
        showToast("Action could not be completed");
      }
    },
    [showToast],
  );

  const ptzContinuous = usePtzContinuousControl({
    cameraId: camera.id,
    operable: ptzOperable && !absolutePtzMode,
    onResult: onPtzDriverResult,
    onError: onPtzDriverError,
  });

  const ptzAbsolute = usePtzAbsoluteStepControl({
    cameraId: camera.id,
    operable: ptzOperable && absolutePtzMode,
    ptzLimits,
    getPtz: () => ptzStateRef.current,
    onResult: onPtzDriverResult,
    onError: onPtzDriverError,
  });

  const ptzDriver = absolutePtzMode ? ptzAbsolute : ptzContinuous;

  useEffect(() => {
    if (!hasPtz || !ptzOperable || !ptzStreamReady) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await fetchPtzState(camera.id);
        if (cancelled) return;
        setPtz(s);
        if (!ptzMovingRef.current) mergeZoomFromHardware(s.zoom);
      } catch {
        /* ignore poll errors */
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 900);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [camera.id, hasPtz, ptzOperable, ptzStreamReady, mergeZoomFromHardware]);

  useEffect(() => {
    if (!hasPtz || !ptzOperable || !ptzStreamReady) {
      setPtzLimits(null);
      return;
    }
    let cancelled = false;
    setPtzLimits(null);
    void (async () => {
      try {
        const limits = await fetchPtzLimits(camera.id);
        if (!cancelled) setPtzLimits(limits);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [camera.id, hasPtz, ptzOperable, ptzStreamReady]);

  useEffect(() => {
    panUnwrapHintRef.current = null;
  }, [camera.id]);

  useEffect(() => {
    if (mainLens !== "ptz") {
      panUnwrapHintRef.current = null;
      return;
    }
    if (!ptzLimits) return;
    if (ptzMoving) return;
    if (panUnwrapHintRef.current === null) {
      const f = focusRef.current;
      panUnwrapHintRef.current =
        (f.normX - 0.5) * (ptzLimits.pan.max - ptzLimits.pan.min) +
        ptzLimits.panOffsetDeg;
    }
    const out = ptzStateToRingNorm(ptz, ptzLimits, panUnwrapHintRef.current);
    panUnwrapHintRef.current = out.panRawUnwrapped;
    const z = Math.round(ptz.zoom);
    setFocus((prev) => {
      if (
        Math.abs(prev.normX - out.normX) < 1e-4 &&
        Math.abs(prev.normY - out.normY) < 1e-4 &&
        Math.round(prev.zoom) === z
      ) {
        return prev;
      }
      persistFocus({ normX: out.normX, normY: out.normY, zoom: z });
      return { normX: out.normX, normY: out.normY, zoom: z };
    });
  }, [mainLens, ptz.pan, ptz.tilt, ptz.zoom, ptzLimits, ptzMoving]);

  /** While main is 360°, sync stored aim with live PTZ (poll / PiP controls). */
  useEffect(() => {
    if (mainLens !== "360") return;
    if (!ptzLimits) return;
    if (ptzMoving) return;
    const z = Math.round(ptz.zoom);
    if (ringDisplayPinnedRef.current) {
      setFocus((prev) => {
        if (Math.round(prev.zoom) === z) return prev;
        const merged = { ...prev, zoom: z };
        persistFocus(merged);
        return merged;
      });
      return;
    }
    const f = focusRef.current;
    const panSpan = ptzLimits.pan.max - ptzLimits.pan.min;
    const panHint = (f.normX - 0.5) * panSpan + ptzLimits.panOffsetDeg;
    const out = ptzStateToRingNorm(ptz, ptzLimits, panHint);
    setFocus((prev) => {
      if (
        Math.abs(prev.normX - out.normX) < 1e-4 &&
        Math.abs(prev.normY - out.normY) < 1e-4 &&
        Math.round(prev.zoom) === z
      ) {
        return prev;
      }
      persistFocus({ normX: out.normX, normY: out.normY, zoom: z });
      return { normX: out.normX, normY: out.normY, zoom: z };
    });
  }, [mainLens, ptz.pan, ptz.tilt, ptz.zoom, ptzLimits, ptzMoving]);

  const atLimit = useMemo<Record<PtzDir, boolean>>(() => {
    if (!ptzLimits) {
      return {
        left: false,
        right: false,
        up: false,
        down: false,
        zoomIn: false,
        zoomOut: false,
      };
    }
    const eps = 1e-3;
    const panEndless =
      Math.abs(ptzLimits.pan.max - ptzLimits.pan.min - 360) < eps;
    return {
      left: panEndless ? false : ptz.pan <= ptzLimits.pan.min + eps,
      right: panEndless ? false : ptz.pan >= ptzLimits.pan.max - eps,
      up: ptz.tilt <= ptzLimits.tilt.min + eps,
      down: ptz.tilt >= ptzLimits.tilt.max - eps,
      zoomIn: ptz.zoom >= ptzLimits.zoom.max - eps,
      zoomOut: ptz.zoom <= ptzLimits.zoom.min - eps,
    };
  }, [ptz.pan, ptz.tilt, ptz.zoom, ptzLimits]);

  const atLimitRef = useRef(atLimit);
  useEffect(() => {
    atLimitRef.current = atLimit;
  }, [atLimit]);

  const onPtzPressStart = useCallback(
    (dir: PtzDir) => {
      if (!ptzOperable || atLimitRef.current[dir]) return;
      ringDisplayPinnedRef.current = false;
      setPtzMoving(true);
      ptzDriver.start(dir);
    },
    [ptzDriver, ptzOperable],
  );

  const onPtzPressEnd = useCallback(() => {
    void ptzDriver.stop().then(() => {
      setPtzMoving(false);
    });
  }, [ptzDriver]);

  const resetZoomInFlightRef = useRef(false);

  const onPtzResetZoom = useCallback(async () => {
    if (!ptzOperable || resetZoomInFlightRef.current) return;
    resetZoomInFlightRef.current = true;
    ringDisplayPinnedRef.current = false;
    setPtzMoving(true);
    await ptzDriver.stop();
    try {
      const next = await ptzResetZoom(camera.id);
      setPtz({ pan: next.pan, tilt: next.tilt, zoom: next.zoom });
      ptzStateRef.current = next;
      mergeZoomFromHardware(next.zoom);
      if (next.hardwareWarning?.trim()) {
        showToast(next.hardwareWarning);
      }
    } catch (e) {
      if (e instanceof ApiRequestError) {
        showToast(e.message || "Zoom reset could not be completed");
      } else {
        showToast("Zoom reset could not be completed");
      }
    } finally {
      resetZoomInFlightRef.current = false;
      setPtzMoving(false);
    }
  }, [camera.id, mergeZoomFromHardware, ptzDriver, ptzOperable]);

  const applyFocusToServer = useCallback(
    async (
      next: { normX: number; normY: number; zoom: number },
      opts?: { ringNorm?: { normX: number; normY: number } },
    ) => {
      if (!hasPtz) {
        showToast("Focus ring mapping requires a PTZ stream");
        return;
      }
      if (!ptzOperable) {
        showToast(
          "PTZ / ONVIF is locked until an admin approves access for you on this camera.",
        );
        return;
      }
      const seq = ++ptzMoveSeqRef.current;
      const startedAt = performance.now();
      const prevState = ptzRef.current;
      setPtzMoving(true);
      try {
        const s = await ptzFocusFromRing(camera.id, next);
        const z = Math.min(25, Math.max(1, Math.round(s.zoom)));
        setPtz({ pan: s.pan, tilt: s.tilt, zoom: s.zoom });
        let ringX = opts?.ringNorm?.normX ?? next.normX;
        let ringY = opts?.ringNorm?.normY ?? next.normY;
        if (!opts?.ringNorm && ptzLimits) {
          const panSpan = ptzLimits.pan.max - ptzLimits.pan.min;
          const panHint = (next.normX - 0.5) * panSpan + ptzLimits.panOffsetDeg;
          const out = ptzStateToRingNorm(s, ptzLimits, panHint);
          ringX = out.normX;
          ringY = out.normY;
        }
        setFocus((prev) => ({
          ...prev,
          normX: ringX,
          normY: ringY,
          zoom: z,
        }));
        persistFocus({ normX: ringX, normY: ringY, zoom: z });
        if (s.hardwareWarning?.trim()) showToast(s.hardwareWarning);
        const totalVisibleMs = Math.min(
          MAX_LOADER_VISIBLE_MS,
          Math.max(
            MIN_LOADER_VISIBLE_MS,
            estimatePtzTravelMs(prevState, s) + HLS_CATCHUP_MS,
          ),
        );
        const elapsedMs = performance.now() - startedAt;
        const remainingMs = totalVisibleMs - elapsedMs;
        if (remainingMs > 0 && seq === ptzMoveSeqRef.current) {
          await new Promise<void>((r) => setTimeout(r, remainingMs));
        }
      } catch (e) {
        if (e instanceof ApiRequestError && e.code === "NO_PTZ_STREAM") {
          showToast("Focus ring mapping requires a PTZ stream");
        } else if (e instanceof ApiRequestError) {
          showToast(e.message || "Could not map focus ring to PTZ");
        } else {
          showToast("Could not map focus ring to PTZ");
        }
      } finally {
        if (seq === ptzMoveSeqRef.current) {
          setPtzMoving(false);
        }
      }
    },
    [camera.id, hasPtz, ptzLimits, ptzOperable, showToast],
  );

  const mainKind: StreamKey = mainLens === "360" ? "360" : "ptz";
  const pipKind: StreamKey = mainLens === "360" ? "ptz" : "360";
  const presetGridLiveEnabled =
    normalizeCameraVendor(camera.vendor) === "seetong" &&
    mainLens === "360" &&
    view360 === "immersive" &&
    hasPtz;

  const mappedPresetGridIds = useMemo(
    () =>
      [...new Set(cameraPresets.map((row) => row.grid_id))]
        .filter((id) => id >= 1 && id <= PRESET_GRID_TOTAL)
        .sort((a, b) => a - b),
    [cameraPresets],
  );

  /** Map immersive 360° pick (0..1 equirectangular) → `POST .../ptz/focus`. */
  const applyPanoramaAimFromNorm = useCallback(
    (normX: number, normY: number) => {
      if (!ptzOperable) return;
      const nx = Math.min(1, Math.max(0, normX));
      const displayNy = Math.min(1, Math.max(0, normY));
      const serverNy = adjustNormYForPtzAim(displayNy);
      const z = ptzZoomSliderMin;
      const display = { normX: nx, normY: displayNy, zoom: z };
      const server = { normX: nx, normY: serverNy, zoom: z };
      ringDisplayPinnedRef.current = true;
      setFocus(display);
      void applyFocusToServer(server, {
        ringNorm: { normX: nx, normY: displayNy },
      });
    },
    [applyFocusToServer, ptzOperable, ptzZoomSliderMin],
  );

  const toggleMainLens = useCallback(() => {
    if (!has360 || !hasPtz) {
      showToast("Both streams are required to switch layout");
      return;
    }
    const next: MainLens = mainLens === "360" ? "ptz" : "360";
    setMainLensState(next);
    persistMainLens(next);
  }, [has360, hasPtz, mainLens, showToast]);

  const pipTapRef = useRef<{ x: number; y: number } | null>(null);

  const isPipControlTarget = useCallback((target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    if (!el) return false;
    return Boolean(
      el.closest(
        "[data-pip-drag-handle], .ds-preview-pip__edge-controls, .ds-ptz-btn, button, input",
      ),
    );
  }, []);

  const toggleView360 = useCallback(() => {
    const next: View360Mode = view360 === "flat" ? "immersive" : "flat";
    setView360State(next);
    persistView360Mode(next);
  }, [view360]);

  const toggleFs = useCallback(async () => {
    const el = rootRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) await el.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      showToast("Fullscreen is not available");
    }
  }, [showToast]);

  const onPipPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("[data-pip-drag-handle]")) {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        setDraggingPip(true);
        pipTapRef.current = null;
        dragRef.current = {
          dx: e.clientX,
          dy: e.clientY,
          startL: pipPx.left,
          startT: pipPx.top,
        };
        return;
      }
      if (isPipControlTarget(e.target)) return;
      pipTapRef.current = { x: e.clientX, y: e.clientY };
    },
    [isPipControlTarget, pipPx.left, pipPx.top],
  );

  const onPipPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (
        !draggingPip ||
        !dragRef.current ||
        stageRect.width <= 0 ||
        stageRect.height <= 0
      )
        return;
      const dx = e.clientX - dragRef.current.dx;
      const dy = e.clientY - dragRef.current.dy;
      const nextLeft = Math.min(
        pipPx.maxLeft,
        Math.max(PIP_EDGE_INSET, dragRef.current.startL + dx),
      );
      const nextTop = Math.min(
        pipPx.maxTop,
        Math.max(PIP_EDGE_INSET, dragRef.current.startT + dy),
      );
      setPipPosState({
        leftPct: nextLeft / stageRect.width,
        topPct: nextTop / stageRect.height,
      });
    },
    [
      draggingPip,
      pipPx.maxLeft,
      pipPx.maxTop,
      stageRect.height,
      stageRect.width,
    ],
  );

  const onPipPointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (draggingPip) {
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        setDraggingPip(false);
        dragRef.current = null;
        pipTapRef.current = null;
        setCachedPreviewPrefs(userId, { pipSharePct, pipPosition: pipPos });
        queuePrefsPut({ pipPosition: pipPos });
        return;
      }
      const tap = pipTapRef.current;
      pipTapRef.current = null;
      if (!tap || isPipControlTarget(e.target)) return;
      const dx = e.clientX - tap.x;
      const dy = e.clientY - tap.y;
      if (dx * dx + dy * dy < 400) {
        toggleMainLens();
      }
    },
    [
      draggingPip,
      isPipControlTarget,
      pipPos,
      pipSharePct,
      queuePrefsPut,
      toggleMainLens,
      userId,
    ],
  );

  const STREAM_AUTO_RETRY_MAX = 4;

  const streamError = useCallback((k: StreamKey) => {
    const nextAttempt = (streamAutoRetryCountRef.current[k] ?? 0) + 1;
    streamAutoRetryCountRef.current[k] = nextAttempt;
    if (nextAttempt <= STREAM_AUTO_RETRY_MAX) {
      setStreamAutoRetry((prev) => ({ ...prev, [k]: prev[k] + 1 }));
      return;
    }
    setStreamFault((prev) => ({ ...prev, [k]: true }));
  }, []);

  const retryStreams = useCallback(() => {
    streamAutoRetryCountRef.current = { "360": 0, ptz: 0 };
    setStreamAutoRetry({ "360": 0, ptz: 0 });
    setStreamFault({ "360": false, ptz: false });
    setRelayBootstrapError(null);
    setRelayUrls(null);
    setReloadKey((k) => k + 1);
  }, []);

  const [controlsHost, setControlsHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setControlsHost(controlsHostRef?.current ?? null);
  }, [controlsHostRef]);

  const controlsRef = useRef<HTMLDivElement>(null);
  const settingsPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!settingsOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const node = settingsPopoverRef.current;
      if (node && !node.contains(e.target as Node)) setSettingsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [settingsOpen]);

  if (!has360 && !hasPtz) {
    return (
      <div className="ds-preview-root">
        <div className="ds-preview-toolbar">
          <h1 className="ds-page-title" style={{ margin: 0 }}>
            {camera.name}
          </h1>
        </div>
        <div className="ds-preview-stream-fallback" role="alert">
          <p>No RTSP URLs configured for this camera.</p>
          <p className="ds-text-secondary" style={{ marginTop: 8 }}>
            Add a 360° or PTZ RTSP URL on the Cameras tab, then return to Live
            View.
          </p>
        </div>
      </div>
    );
  }

  const renderStreamBlock = (kind: StreamKey, isMain: boolean) => {
    const has = kind === "360" ? has360 : hasPtz;
    const fault = streamFault[kind];
    const streamType: StreamTransport =
      kind === "360" ? stream360Transport : streamPtzTransport;
    const playlistUrl = relayUrls
      ? resolveRelayStreamUrl(relayUrls, kind, streamType)
      : null;
    if (!has) {
      return (
        <div className="ds-preview-stream-fallback">
          <p>
            {kind === "360"
              ? "No 360° RTSP configured"
              : "No PTZ RTSP configured"}
          </p>
        </div>
      );
    }
    if (relayBootstrapError && relayUrls === null) {
      return (
        <div className="ds-preview-stream-fallback" role="alert">
          <p>Camera feed unavailable</p>
          <p className="ds-text-secondary" style={{ fontSize: 12 }}>
            {relayBootstrapError}
          </p>
          <button
            type="button"
            className="ds-btn ds-btn--ghost"
            onClick={retryStreams}
          >
            Retry
          </button>
        </div>
      );
    }
    if (relayUrls === null) {
      return (
        <div className="ds-preview-stream-wrap">
          <PreviewActivityBar visible label="Starting camera stream" />
          <div className="ds-preview-buffer ds-preview-buffer--bootstrap">
            <span className="ds-preview-buffer__label">Starting stream…</span>
          </div>
        </div>
      );
    }
    if (fault) {
      return (
        <div className="ds-preview-stream-fallback" role="alert">
          <p>Camera feed unavailable</p>
          <button
            type="button"
            className="ds-btn ds-btn--ghost"
            onClick={retryStreams}
          >
            Retry
          </button>
        </div>
      );
    }
    if (!playlistUrl) {
      return (
        <div className="ds-preview-stream-fallback">
          <p>Stream is currently unavailable. Please try again later.</p>
        </div>
      );
    }
    const usePanoWebgl =
      kind === "360" &&
      isMain &&
      mainLens === "360" &&
      view360 === "immersive" &&
      !fault;
    const showActivityBar = buffering[kind] || streamInitializing[kind];
    const activityLabel = buffering[kind]
      ? "Buffering video"
      : "Loading stream";
    const streamObjectFit = kind === "ptz" ? "contain" : "cover";
    return (
      <div className="ds-preview-stream-wrap">
        <PreviewActivityBar visible={showActivityBar} label={activityLabel} />
        {usePanoWebgl ? (
          <Pano360WebGL
            key={`${kind}-${reloadKey}-${streamAutoRetry[kind]}-${streamType}-${playlistUrl}-pano`}
            playlistUrl={playlistUrl}
            streamType={streamType}
            enabled
            pickingEnabled={
              !presetGridLiveEnabled &&
              ptzOperable &&
              flatPanoramaAimEnabled &&
              hasPtz
            }
            onEquirectPick={
              presetGridLiveEnabled ? undefined : applyPanoramaAimFromNorm
            }
            focusRing={
              hasPtz
                ? {
                    normX: focus.normX,
                    normY: focus.normY,
                    diameterFrac: focusRingFrac,
                  }
                : null
            }
            externalAssetHotspots={assetHotspots.length > 0}
            onImmersiveSceneReady={
              isMain ? handleImmersiveSceneReady : undefined
            }
            presetGridEnabled={presetGridLiveEnabled}
            mappedPresetGridIds={mappedPresetGridIds}
            presetGridMarkerPickOnly={presetGridLiveEnabled}
            selectedPresetGridId={selectedPresetGridId}
            onPresetGridSelect={handlePresetGridSelect}
            onStreamError={() => streamError(kind)}
            onBufferingChange={(busy) =>
              setBuffering((b) => ({ ...b, [kind]: busy }))
            }
            onStreamLoadingChange={(loading) =>
              setStreamInitializingFor(kind, loading)
            }
          />
        ) : (
          <StreamVideo
            key={`${kind}-${reloadKey}-${streamAutoRetry[kind]}-${streamType}-${playlistUrl}`}
            playlistUrl={playlistUrl}
            streamType={streamType}
            enabled
            objectFit={streamObjectFit}
            onStreamError={() => streamError(kind)}
            onBufferingChange={(busy) =>
              setBuffering((b) => ({ ...b, [kind]: busy }))
            }
            onStreamLoadingChange={(loading) =>
              setStreamInitializingFor(kind, loading)
            }
          />
        )}
        {buffering[kind] ? (
          <div
            className="ds-preview-buffer ds-preview-buffer--light"
            aria-hidden
          >
            <span className="ds-preview-buffer__label">Buffering…</span>
          </div>
        ) : null}
        {usePanoWebgl && isMain && showImmersiveAssetHotspots ? (
          <PatrolAssetHotspotsLayer
            enabled
            hotspots={assetHotspots}
            hostRef={immersiveHostRef}
            canvasRef={immersiveCanvasRef}
            cameraRef={immersiveCameraRef}
            onViewCameraOpenChange={setHotspotViewOpen}
          />
        ) : null}
      </div>
    );
  };

  const mainImmersiveClass =
    mainLens === "360" && view360 === "immersive"
      ? "ds-preview-main--immersive"
      : "";

  const isFullscreen =
    typeof document !== "undefined" && Boolean(document.fullscreenElement);
  const view360Label =
    view360 === "flat" ? "Switch to 360° view" : "Switch to flat view";
  const fsLabel = isFullscreen ? "Exit fullscreen" : "Enter fullscreen";

  const activeStreamTransport =
    mainKind === "360" ? stream360Transport : streamPtzTransport;

  const showStreamTransportToggle =
    (mainKind === "360" && has360) || (mainKind === "ptz" && hasPtz);

  const previewControls = (
    <div className="ds-preview-controls" ref={controlsRef}>
      {mainLens === "360" && has360 ? (
        <button
          type="button"
          className="ds-btn ds-btn--icon ds-btn--secondary"
          onClick={toggleView360}
          title={view360Label}
          aria-label={view360Label}
        >
          {view360 === "flat" ? <Icon360 /> : <IconFlat />}
        </button>
      ) : null}
      {showStreamTransportToggle ? (
        <button
          type="button"
          className="ds-btn ds-btn--secondary"
          onClick={() => {
            if (mainKind === "360") {
              setStream360Transport((prev) =>
                prev === "hls" ? "webrtc" : "hls",
              );
            } else {
              setStreamPtzTransport((prev) =>
                prev === "hls" ? "webrtc" : "hls",
              );
            }
          }}
          title={`Switch ${mainKind} transport (current: ${activeStreamTransport.toUpperCase()})`}
          aria-label={`Switch ${mainKind} transport (current: ${activeStreamTransport.toUpperCase()})`}
        >
          {activeStreamTransport === "hls" ? "HLS" : "WebRTC"}
        </button>
      ) : null}
      {has360 && hasPtz ? (
        <button
          type="button"
          className="ds-btn ds-btn--icon ds-btn--primary"
          onClick={toggleMainLens}
          title="Switch camera"
          aria-label="Switch camera"
        >
          <IconSwitchCamera />
        </button>
      ) : null}
      <button
        type="button"
        className="ds-btn ds-btn--icon ds-btn--ghost"
        onClick={() => void toggleFs()}
        title={fsLabel}
        aria-label={fsLabel}
      >
        {isFullscreen ? <IconExitFullscreen /> : <IconFullscreen />}
      </button>
      {has360 && hasPtz ? (
        <button
          type="button"
          className="ds-btn ds-btn--icon ds-btn--secondary"
          onClick={() => {
            setPipPopupHiddenState((h) => {
              const next = !h;
              persistPipPopupHidden(next);
              return next;
            });
          }}
          title={
            pipPopupHidden
              ? "Show small preview (picture-in-picture)"
              : "Hide small preview (picture-in-picture)"
          }
          aria-label={
            pipPopupHidden
              ? "Show small preview picture in picture"
              : "Hide small preview picture in picture"
          }
          aria-pressed={pipPopupHidden}
        >
          {pipPopupHidden ? <IconPipPopupHidden /> : <IconPipPopupVisible />}
        </button>
      ) : null}
      <div className="ds-preview-popover" ref={settingsPopoverRef}>
        <button
          type="button"
          className="ds-btn ds-btn--icon ds-btn--ghost"
          onClick={() => {
            setSettingsOpen((o) => !o);
          }}
          title="Preview size"
          aria-label="Preview size"
          aria-expanded={settingsOpen}
          aria-haspopup="dialog"
        >
          <IconSliders />
        </button>
        {settingsOpen ? (
          <div
            className="ds-preview-popover__panel ds-preview-settings"
            role="dialog"
            aria-label="Small preview size"
          >
            <div className="ds-preview-settings__row">
              <label>
                Small preview size ({pipSharePct}% of main)
                <input
                  type="range"
                  min={PIP_SHARE_MIN}
                  max={PIP_SHARE_MAX}
                  step={1}
                  value={pipSharePct}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setPipSharePctState(next);
                    setCachedPreviewPrefs(userId, {
                      pipSharePct: next,
                      pipPosition: pipPos,
                    });
                    queuePrefsPut({ pipSharePct: next });
                  }}
                />
              </label>
              <p
                className="ds-text-secondary"
                style={{ margin: 0, fontSize: 12 }}
              >
                Main preview keeps the remaining {100 - pipSharePct}%. Sizes
                adapt automatically to browser, resolution, and fullscreen.
              </p>
            </div>
            <div className="ds-preview-settings__row">
              <label>
                PTZ click buffer (max {bufferMax})
                <input
                  type="range"
                  min={PTZ_BUFFER_MAX_MIN}
                  max={PTZ_BUFFER_MAX_MAX}
                  step={1}
                  value={bufferMax}
                  onChange={(e) => {
                    const next = Math.min(
                      PTZ_BUFFER_MAX_MAX,
                      Math.max(PTZ_BUFFER_MAX_MIN, Number(e.target.value)),
                    );
                    setBufferMaxState(next);
                    setCachedPtzPrefs(userId, { bufferMax: next });
                    queuePtzPrefsPut({ bufferMax: next });
                  }}
                />
              </label>
              <p
                className="ds-text-secondary"
                style={{ margin: 0, fontSize: 12 }}
              >
                How many clicks each PTZ movement button can buffer while the
                camera is moving. Range {PTZ_BUFFER_MAX_MIN}–
                {PTZ_BUFFER_MAX_MAX}; lowering this caps the counter on every
                direction button.
              </p>
            </div>
            <button
              type="button"
              className="ds-btn ds-btn--ghost"
              onClick={() => setSettingsOpen(false)}
            >
              Close
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`ds-preview-root${
        hotspotViewOpen ? " ds-preview-root--nearby-view-open" : ""
      }`}
    >
      {accessBannerText ? (
        <div
          className="ds-preview-access-banner"
          role="status"
          style={{
            marginBottom: "var(--space-3)",
            padding: "10px 12px",
            borderRadius: "var(--radius-control)",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
          }}
        >
          <p className="ds-body" style={{ margin: 0 }}>
            {accessBannerText}
          </p>
        </div>
      ) : null}
      <div
        ref={stageRef}
        className={`ds-preview-stage ${
          mainLens === "360" && view360 === "flat"
            ? "ds-preview-stage--panorama"
            : "ds-preview-stage--standard"
        }`}
      >
        {has360 &&
        hasPtz &&
        !pipPopupHidden &&
        pipPx.width > 0 &&
        pipPx.height > 0 ? (
          <>
            <div
              className={[
                "ds-preview-lens-slot",
                mainLens === "360"
                  ? `ds-preview-lens-slot--main ${mainImmersiveClass}`
                  : "ds-preview-lens-slot--pip ds-preview-pip ds-preview-pip--swappable",
              ]
                .filter(Boolean)
                .join(" ")}
              style={
                mainLens === "360"
                  ? undefined
                  : {
                      width: pipPx.width,
                      height: pipPx.height,
                      left: pipPx.left,
                      top: pipPx.top,
                    }
              }
              onPointerDown={mainLens === "ptz" ? onPipPointerDown : undefined}
              onPointerMove={mainLens === "ptz" ? onPipPointerMove : undefined}
              onPointerUp={mainLens === "ptz" ? onPipPointerUp : undefined}
              onPointerCancel={mainLens === "ptz" ? onPipPointerUp : undefined}
            >
              <div className="ds-preview-pip__player">
                {mainLens === "ptz" ? (
                  <div
                    className="ds-preview-pip__chrome ds-preview-pip__chrome--overlay"
                    data-pip-drag-handle
                  >
                    <span>360°</span>
                  </div>
                ) : null}
                {mainLens === "360" && view360 === "flat" ? (
                  <div
                    className="ds-preview-flat-surface"
                    role="region"
                    aria-label={
                      ptzOperable
                        ? "360° flat panorama"
                        : "360° flat panorama — PTZ control locked until access is approved"
                    }
                  >
                    <div className="ds-preview-flat-surface__media">
                      {renderStreamBlock("360", true)}
                    </div>
                    <PanoramaHorizonLine variant="main" />
                    <PtzMoveLoader visible={ptzMoving} />
                  </div>
                ) : mainLens === "ptz" ? (
                  <div
                    className="ds-preview-pip-flat-surface"
                    role="region"
                    aria-label={
                      ptzOperable
                        ? "360° overview"
                        : "360° overview — PTZ control locked until access is approved"
                    }
                  >
                    <div className="ds-preview-pip-flat-surface__media">
                      {renderStreamBlock("360", false)}
                    </div>
                    <PanoramaHorizonLine variant="pip" />
                  </div>
                ) : (
                  renderStreamBlock("360", true)
                )}
                {mainLens === "360" && view360 === "immersive" ? (
                  <PtzMoveLoader visible={ptzMoving} />
                ) : null}
              </div>
            </div>
            <div
              className={[
                "ds-preview-lens-slot",
                mainLens === "ptz"
                  ? "ds-preview-lens-slot--main"
                  : "ds-preview-lens-slot--pip ds-preview-pip ds-preview-pip--swappable",
              ].join(" ")}
              style={
                mainLens === "ptz"
                  ? undefined
                  : {
                      width: pipPx.width,
                      height: pipPx.height,
                      left: pipPx.left,
                      top: pipPx.top,
                    }
              }
              onPointerDown={mainLens === "360" ? onPipPointerDown : undefined}
              onPointerMove={mainLens === "360" ? onPipPointerMove : undefined}
              onPointerUp={mainLens === "360" ? onPipPointerUp : undefined}
              onPointerCancel={mainLens === "360" ? onPipPointerUp : undefined}
            >
              <div className="ds-preview-pip__player">
                {mainLens === "360" ? (
                  <div
                    className="ds-preview-pip__chrome ds-preview-pip__chrome--overlay"
                    data-pip-drag-handle
                  >
                    <span>PTZ</span>
                    <span className="ds-preview-pip__metrics">
                      Pan {ptz.pan.toFixed(1)}° | Tilt {ptz.tilt.toFixed(1)}° |
                      Zoom {Math.round(ptz.zoom)}×
                    </span>
                  </div>
                ) : null}
                {renderStreamBlock("ptz", mainLens === "ptz")}
                {mainLens === "ptz" ? (
                  <PtzMoveLoader visible={ptzMoving} />
                ) : null}
                {mainLens === "ptz" && !streamFault.ptz ? (
                  <div className="ds-preview-ptz-fs-overlay">
                    <div
                      ref={ptzWheelSurfaceRef}
                      className="ds-preview-ptz-wheel-surface"
                      aria-hidden
                    />
                    <div
                      className="ds-preview-ptz-fs-metrics"
                      aria-live="polite"
                    >
                      Pan {ptz.pan.toFixed(1)}° | Tilt {ptz.tilt.toFixed(1)}° |
                      Zoom {Math.round(ptz.zoom)}×
                    </div>
                    <PtzGamepadDpad
                      placement="bottom-right"
                      counts={ptzDriver.counts}
                      isFull={ptzDriver.isFull}
                      atLimit={atLimit}
                      operable={ptzOperable}
                      onPressStart={onPtzPressStart}
                      onPressEnd={onPtzPressEnd}
                    />
                    <div className="ds-preview-ptz-fs-zoom">
                      <PtzZoomPanel
                        inPlayer
                        ptz={ptz}
                        ptzOperable={ptzOperable}
                        sliderMin={ptzZoomSliderMin}
                        sliderMax={ptzZoomSliderMax}
                        onZoomCommit={onPtzZoomCommit}
                        onResetZoom={() => void onPtzResetZoom()}
                        onZoomPressStart={onPtzPressStart}
                        onZoomPressEnd={onPtzPressEnd}
                        atLimit={{
                          zoomIn: atLimit.zoomIn,
                          zoomOut: atLimit.zoomOut,
                        }}
                      />
                    </div>
                  </div>
                ) : null}
                {mainLens === "360" && !streamFault.ptz ? (
                  <div
                    className="ds-preview-pip__edge-controls"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <div
                      className="ds-preview-pip__edge-pad-layer"
                      aria-hidden={
                        pipSharePct < PIP_SHARE_SHOW_PTZ_EDGE_ARROWS_AT
                      }
                    >
                      {pipSharePct >= PIP_SHARE_SHOW_PTZ_EDGE_ARROWS_AT ? (
                        <PtzEdgeDirectionPad
                          counts={ptzDriver.counts}
                          isFull={ptzDriver.isFull}
                          atLimit={atLimit}
                          operable={ptzOperable}
                          onPressStart={onPtzPressStart}
                          onPressEnd={onPtzPressEnd}
                        />
                      ) : null}
                    </div>
                    <div className="ds-preview-pip__pip-zoom-bar">
                      <PtzZoomPanel
                        inPlayer
                        compact
                        ptz={ptz}
                        ptzOperable={ptzOperable}
                        sliderMin={ptzZoomSliderMin}
                        sliderMax={ptzZoomSliderMax}
                        onZoomCommit={onPtzZoomCommit}
                        onResetZoom={() => void onPtzResetZoom()}
                        onZoomPressStart={onPtzPressStart}
                        onZoomPressEnd={onPtzPressEnd}
                        atLimit={{
                          zoomIn: atLimit.zoomIn,
                          zoomOut: atLimit.zoomOut,
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <div className={`ds-preview-main ${mainImmersiveClass}`}>
            {mainLens === "360" && view360 === "flat" ? (
              <div
                className="ds-preview-flat-surface"
                role="region"
                aria-label={
                  ptzOperable
                    ? "360° flat panorama"
                    : "360° flat panorama — PTZ control locked until access is approved"
                }
              >
                <div className="ds-preview-flat-surface__media">
                  {renderStreamBlock("360", true)}
                </div>
                <PanoramaHorizonLine variant="main" />
                <PtzMoveLoader visible={ptzMoving} />
              </div>
            ) : (
              <>
                {renderStreamBlock(mainKind, true)}
                {mainLens === "ptz" ? (
                  <PtzMoveLoader visible={ptzMoving} />
                ) : null}
              </>
            )}

            {mainLens === "360" && view360 === "immersive" ? (
              <PtzMoveLoader visible={ptzMoving} />
            ) : null}

            {mainLens === "ptz" && !streamFault.ptz && hasPtz ? (
              <div className="ds-preview-ptz-fs-overlay">
                <div
                  ref={ptzWheelSurfaceRef}
                  className="ds-preview-ptz-wheel-surface"
                  aria-hidden
                />
                <div className="ds-preview-ptz-fs-metrics" aria-live="polite">
                  Pan {ptz.pan.toFixed(1)}° | Tilt {ptz.tilt.toFixed(1)}° | Zoom{" "}
                  {Math.round(ptz.zoom)}×
                </div>
                <PtzGamepadDpad
                  placement="bottom-right"
                  counts={ptzDriver.counts}
                  isFull={ptzDriver.isFull}
                  atLimit={atLimit}
                  operable={ptzOperable}
                  onPressStart={onPtzPressStart}
                  onPressEnd={onPtzPressEnd}
                />
                <div className="ds-preview-ptz-fs-zoom">
                  <PtzZoomPanel
                    inPlayer
                    ptz={ptz}
                    ptzOperable={ptzOperable}
                    sliderMin={ptzZoomSliderMin}
                    sliderMax={ptzZoomSliderMax}
                    onZoomCommit={onPtzZoomCommit}
                    onResetZoom={() => void onPtzResetZoom()}
                    onZoomPressStart={onPtzPressStart}
                    onZoomPressEnd={onPtzPressEnd}
                    atLimit={{
                      zoomIn: atLimit.zoomIn,
                      zoomOut: atLimit.zoomOut,
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}

        {
          (controlsHost
            ? createPortal(previewControls, controlsHost)
            : controlsHostRef
              ? null
              : previewControls) as React.ReactNode
        }
      </div>

      {hotspotViewOpen ? (
        <div
          className="ds-preview-nearby-view-scrim"
          role="presentation"
          aria-hidden
        />
      ) : null}

      {toast ? (
        <div className="ds-preview-toast" role="status">
          {toast}
        </div>
      ) : null}

    </div>
  );
}
